/* eslint-disable no-console */
import { useCallback, useEffect, useMemo } from 'react';

import { useCacheQuery } from '../../../hooks/use-cache-query';
import {
  BaseSignalRMessage,
  SignalRFilterKey,
  SignalRScheduleMessage,
} from '../../../hooks/use-signalr';
import { useAppContext } from '../../context/app-context';
import {
  GetScheduleItemsDocument,
  GetScheduleItemsQueryVariables,
  PushType,
  ScheduleItemDataFragment,
} from '../../types/api/graphql';
import { getDatesOverlap } from '../../utils/date.utils';
import { PracticeId } from '../types';

export function useScheduleItems(
  variables: GetScheduleItemsQueryVariables,
  disableSignalR?: boolean
) {
  const {
    data: scheduleItemsData,
    loading: scheduleItemsLoading,
    refetch,
  } = useCacheQuery(GetScheduleItemsDocument, {
    variables: {
      ...variables,
      healthProfessionalId:
        variables.healthProfessionalId === PracticeId
          ? null
          : variables.healthProfessionalId,
    },
    skip:
      !variables.healthProviderId ||
      !variables.healthProfessionalId ||
      !variables.startDate ||
      !variables.endDate,
  });

  const { signalRMessage, addFilter } = useAppContext();

  const filter = useCallback(
    (message: BaseSignalRMessage) => {
      if (disableSignalR) return false;

      if (
        [PushType.ScheduleUpdated, PushType.AgendaTemplateApplied].includes(
          message.type
        )
      ) {
        const { healthProfessionalId, healthProviderId, startDate, endDate } =
          message as SignalRScheduleMessage;
        if (
          (healthProfessionalId === variables.healthProfessionalId ||
            variables.healthProfessionalId === PracticeId) &&
          healthProviderId === variables.healthProviderId
        ) {
          const formattedSignalRStartDate = new Date(startDate);
          const formattedSignalREndDate = new Date(endDate);
          const formattedStartDate = new Date(variables.startDate);
          const formattedREndDate = new Date(variables.endDate);
          if (
            getDatesOverlap(
              { start: formattedStartDate, end: formattedREndDate },
              { start: formattedSignalRStartDate, end: formattedSignalREndDate }
            )
          ) {
            return true;
          }
        }
      }

      return false;
    },
    [
      disableSignalR,
      variables.healthProfessionalId,
      variables.healthProviderId,
      variables.startDate,
      variables.endDate,
    ]
  );

  useEffect(() => {
    if (disableSignalR === true) return;
    if (scheduleItemsLoading) return;
    if (variables.healthProviderId) {
      addFilter({ key: SignalRFilterKey.SCHEDULE, filter });
    }
  }, [
    addFilter,
    disableSignalR,
    filter,
    scheduleItemsLoading,
    variables.healthProviderId,
  ]);

  useEffect(() => {
    if (signalRMessage && filter(signalRMessage)) {
      console.log(`[${SignalRFilterKey.SCHEDULE}] refetch`);
      refetch();
    }
  }, [filter, refetch, signalRMessage]);

  const data = useMemo(
    () =>
      (scheduleItemsData?.schedules ?? []).map(
        option => option as ScheduleItemDataFragment
      ),
    [scheduleItemsData?.schedules]
  );

  return {
    data,
    loading: scheduleItemsLoading,
  };
}
